import React from 'react';
import { Box } from '@mui/material';
import { PlanStep, Participant } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import StepInteractionFeed, { FeedItem } from '../StepInteractionFeed';
import DelegateTaskControl from '../../../shared/DelegateTaskControl';
import CollapsibleStepInfo from '../CollapsibleStepInfo';

interface WaitingOnDelegateContentProps {
  planId: string;
  step: PlanStep;
  participants?: Participant[];
  feedItems: FeedItem[];
}

/**
 * WAITING_ON_DELEGATE mode content.
 * There is an active delegated request that is not yet satisfied.
 *
 * Renders:
 * - Delegation card showing assignee, status, actions (Reassign/Cancel)
 * - Compact status line ("Waiting on delegate")
 *
 * Does NOT render:
 * - "Mark complete"
 * - Completion progress UI
 * - Delegation input fields
 * - Chat as a primary action
 */
export const WaitingOnDelegateContent: React.FC<WaitingOnDelegateContentProps> = ({
  planId,
  step,
  participants = [],
  feedItems,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Collapsible Step Info - auto-expands first time */}
      {step.description && (
        <CollapsibleStepInfo stepId={step.id} description={step.description} />
      )}

      {/* Delegation status card - primary content */}
      <Box sx={{ py: 1 }}>
        <DelegateTaskControl
          planId={planId}
          step={step}
          participants={participants}
          showStatusOnly={true}
        />
      </Box>

      {/* Minimal feed - just description and recent activity */}
      <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
        <StepInteractionFeed items={feedItems} stepTitle={step.title} />
      </Box>
    </Box>
  );
};

export default WaitingOnDelegateContent;
